import { RfqTemplateType } from '@lune-fe/dapi-models'
import { Switch, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Stack } from '@mui/material'
import FieldValidationError from 'lune-ui-lib/src/components/FieldValidationError'
import { useSnackbar } from 'notistack'
import React, { ReactNode, useEffect, useRef, useState } from 'react'

import useTitle from 'hooks/useTitle'
import AutoFillLabel from 'views/CalculateEmissions/RFQ/AutoFillLabel'
import Labels from 'views/CalculateEmissions/RFQ/components/Labels'
import { StageContext } from 'views/CalculateEmissions/RFQ/components/MultiStepFlow'
import ParameterMapping from 'views/CalculateEmissions/RFQ/components/ParameterMapping'
import ProgressSteps, { MappingSteps } from 'views/CalculateEmissions/RFQ/components/ProgressSteps'
import SharedLayout from 'views/CalculateEmissions/RFQ/components/SharedLayout'
import { getMappingInfo } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import { LoadUnit, PropertyMapping } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import {
    PARAMETER_MAPPING_OUTPUT,
    RFQState,
    RFQTemplateState,
} from 'views/CalculateEmissions/RFQ/utils/types'
import { showClientAccountsStep } from 'views/CalculateEmissions/RFQ/utils/utils'

const ShippingDetails = ({
    sheetType,
    context,
}: {
    sheetType: RfqTemplateType
    context: StageContext
}) => {
    const { title } = getMappingInfo(sheetType)
    useTitle(`Load - ${title}`)
    const { enqueueSnackbar: snackbar } = useSnackbar()

    const [state, setState] = useState<RFQTemplateState | undefined>()
    const [shipmentLoadMapping, setShippingDetailsMapping] = useState<
        PropertyMapping<LoadUnit> | undefined
    >()
    const [shipmentLoadSizeMapping, setShippingDetailsSizeMapping] = useState<
        PropertyMapping<number> | undefined
    >()
    const [shippedAtMapping, setShippedAtMapping] = useState<PropertyMapping<string> | undefined>()
    const [shipmentIdMapping, setShipmentIdMapping] = useState<
        PropertyMapping<string> | undefined
    >()
    const [loadColumnAutoFilled, setLoadColumnAutoFilled] = useState<boolean>(false)
    const [loadUnitAutoFilled, setLoadUnitAutoFilled] = useState<boolean>(false)
    const [loadSizeColumnAutoFilled, setLoadSizeColumnAutoFilled] = useState<boolean>(false)
    const [shippedAtColumnAutoFilled, setShippedAtColumnAutoFilled] = useState<boolean>(false)
    const [shipmentIdColumnAutoFilled, setShipmentIdColumnAutoFilled] = useState<boolean>(false)
    const [shipmentIdAsIdempotencyKeyMapping, setShipmentIdAsIdempotencyKeyMapping] = useState<
        PropertyMapping<boolean> | undefined
    >()
    const [error, setError] = useState<string | undefined>()
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (!context.state) {
            snackbar(`File could not be found.`)
            return
        }
        const storedState = context.state
        if (!storedState.sheet) {
            throw new Error(`Programming error. State should be filled and with data`)
        }
        setState(storedState as RFQTemplateState)

        setShippedAtMapping(storedState.shippedAt)
        setShipmentIdMapping(storedState.shipmentId)
        const storedLoad = storedState.load
        // Separate shipment size and shipment load for presentation.
        if (
            storedLoad !== undefined &&
            storedLoad.source === 'column' &&
            storedLoad.valueMapping.source === 'unit' &&
            storedLoad.valueMapping.unit === 'TEU'
        ) {
            setShippingDetailsMapping(undefined)
            setShippingDetailsSizeMapping({
                source: 'column',
                columnIndex: storedLoad.columnIndex,
                valueMapping: { source: 'direct' },
            })

            // Move auto fill information from load to loadSize
            setLoadColumnAutoFilled(false)
            setLoadUnitAutoFilled(false)
            setLoadSizeColumnAutoFilled(!!storedState.autoFillState?.load?.column)
        } else {
            setShippingDetailsMapping(storedState.load)
            setShippingDetailsSizeMapping(storedState.loadSize)
            setLoadColumnAutoFilled(!!storedState.autoFillState?.load?.column)
            setLoadUnitAutoFilled(!!storedState.autoFillState?.load?.mapping)
            setLoadSizeColumnAutoFilled(!!storedState.autoFillState?.loadSize?.column)
        }
        setShippedAtColumnAutoFilled(!!storedState.autoFillState?.shippedAt?.column)
        setShipmentIdColumnAutoFilled(!!storedState.autoFillState?.shipmentId?.column)
        setShipmentIdAsIdempotencyKeyMapping(
            storedState.shipmentIdAsIdempotencyKey ??
                ({ source: 'constant', value: false } as PropertyMapping<boolean>),
        )
    }, [])

    const handleSubmit = (e: React.FormEvent) => {
        if (!state) return
        e.preventDefault()
        if (!shipmentLoadMapping && !shipmentLoadSizeMapping) {
            return
        }

        const newState: RFQState = {
            ...state,
            load: shipmentLoadMapping,
            loadSize: shipmentLoadSizeMapping,
            shippedAt: shippedAtMapping,
            shipmentId: shipmentIdMapping,
            currentLeg: 1,
            autoFillState: {
                ...state.autoFillState,
                load: { column: loadColumnAutoFilled, mapping: loadUnitAutoFilled },
                loadSize: { column: loadSizeColumnAutoFilled, mapping: false },
                shippedAt: { column: shippedAtColumnAutoFilled, mapping: false },
                shipmentId: { column: shipmentIdColumnAutoFilled, mapping: false },
            },
            shipmentIdAsIdempotencyKey: shipmentIdAsIdempotencyKeyMapping,
        } as RFQState
        context.updateState(newState)
        context.navigate('leg')
    }

    useEffect(() => {
        if (!shipmentLoadMapping && !shipmentLoadSizeMapping) {
            setError('Required mapping')
        } else {
            setError(undefined)
        }
    }, [shipmentLoadMapping, shipmentLoadSizeMapping])

    function createSubsection(title: string, subTitle: string, children: ReactNode): ReactNode {
        return (
            <Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Text variant="h6">{title}</Text>
                </Stack>
                <Text
                    variant="body3"
                    sx={{
                        color: `Grey600`,
                        display: 'block',
                        marginTop: '8px !important',
                    }}
                >
                    {subTitle}
                </Text>
                <Box sx={{ mt: 4 }}>
                    <Labels />
                </Box>
                {children}
            </Box>
        )
    }

    const showClientAccounts = showClientAccountsStep(state)

    return (
        <>
            <SharedLayout
                loading={!state}
                stepsComponent={
                    <>
                        <ProgressSteps
                            step={MappingSteps.LOAD}
                            showClientAccounts={showClientAccounts}
                        />
                        <Text variant={'h4'}>Shipping details</Text>
                    </>
                }
                handleNext={() => {
                    if (formRef.current) {
                        formRef.current.requestSubmit()
                    }
                }}
                handlePrev={
                    showClientAccounts ? () => context.navigate('clientAccountDetails') : undefined
                }
                prevButtonLabel={showClientAccounts ? 'Client accounts' : undefined}
                nextButtonLabel={'Leg 1'}
            >
                {state && (
                    <>
                        {(loadColumnAutoFilled || loadUnitAutoFilled) && <AutoFillLabel />}
                        <form ref={formRef} onSubmit={handleSubmit}>
                            <Stack key="shippingDetails" spacing="72px" sx={{ mt: 4, mb: 3 }}>
                                {createSubsection(
                                    'Load',
                                    'At least one column must be mapped',
                                    <>
                                        <ParameterMapping<number>
                                            state={state}
                                            parameter="loadSize"
                                            mappingType="direct"
                                            outputValues={[]}
                                            formatOutput={String}
                                            propertyMapping={shipmentLoadSizeMapping}
                                            onUpdate={(_parameterName, data) => {
                                                setError(undefined)
                                                setShippingDetailsSizeMapping(data)
                                            }}
                                            autoFill={{
                                                column: {
                                                    value: loadSizeColumnAutoFilled,
                                                    update: setLoadSizeColumnAutoFilled,
                                                },
                                                mapping: { value: false, update: () => {} },
                                            }}
                                        />
                                        <ParameterMapping<LoadUnit>
                                            state={state}
                                            parameter="load"
                                            mappingType="unit"
                                            outputValues={PARAMETER_MAPPING_OUTPUT.load}
                                            formatOutput={String}
                                            propertyMapping={shipmentLoadMapping}
                                            onUpdate={(_parameterName, data) => {
                                                setError(undefined)
                                                setShippingDetailsMapping(data)
                                            }}
                                            autoFill={{
                                                column: {
                                                    value: loadColumnAutoFilled,
                                                    update: setLoadColumnAutoFilled,
                                                },
                                                mapping: {
                                                    value: loadUnitAutoFilled,
                                                    update: setLoadUnitAutoFilled,
                                                },
                                            }}
                                        />
                                        {error && <FieldValidationError error={error} />}
                                    </>,
                                )}

                                {createSubsection(
                                    'Shipment ID',
                                    'Optional',
                                    <>
                                        <ParameterMapping<string>
                                            state={state}
                                            parameter="shipmentId"
                                            mappingType="direct"
                                            outputValues={[]}
                                            formatOutput={String}
                                            propertyMapping={shipmentIdMapping}
                                            onUpdate={(_parameterName, data) => {
                                                setShipmentIdMapping(data)
                                            }}
                                            autoFill={{
                                                column: {
                                                    value: shipmentIdColumnAutoFilled,
                                                    update: setShipmentIdColumnAutoFilled,
                                                },
                                                mapping: { value: false, update: () => {} },
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                mt: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            <Switch
                                                checked={
                                                    shipmentIdAsIdempotencyKeyMapping?.source ===
                                                    'constant'
                                                        ? shipmentIdAsIdempotencyKeyMapping.value
                                                        : false
                                                }
                                                onChange={(e) => {
                                                    setShipmentIdAsIdempotencyKeyMapping({
                                                        source: 'constant',
                                                        value: e.target.checked,
                                                    } as PropertyMapping<boolean>)
                                                }}
                                            />
                                            <Text variant="button">
                                                Only calculate each Shipment ID once, unless data
                                                changes
                                            </Text>
                                            <Tooltip
                                                title="If you upload a file with the same Shipment ID later, it won't be recalculated, unless the data or mapping has changed"
                                                placement="top"
                                                contentWidth={true}
                                            >
                                                <Box p={1.5} sx={{ cursor: 'pointer' }}>
                                                    <InfoOutlinedIcon />
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </>,
                                )}

                                {createSubsection(
                                    'Shipment date',
                                    'Optional',
                                    <ParameterMapping<string>
                                        state={state}
                                        parameter="shippedAt"
                                        mappingType="direct"
                                        outputValues={[]}
                                        formatOutput={String}
                                        propertyMapping={shippedAtMapping}
                                        onUpdate={(_parameterName, data) => {
                                            setShippedAtMapping(data)
                                        }}
                                        autoFill={{
                                            column: {
                                                value: shippedAtColumnAutoFilled,
                                                update: setShippedAtColumnAutoFilled,
                                            },
                                            mapping: { value: false, update: () => {} },
                                        }}
                                    />,
                                )}
                            </Stack>
                        </form>
                    </>
                )}
            </SharedLayout>
        </>
    )
}

export default ShippingDetails
